import React, { useEffect } from "react";
import type { ExitTicketAttemptDetail, ExitTicketData } from "../../types/data";
import { useScrollHint } from "../../../../hooks/useScrollHint";
import AssessmentAttemptCard from "./AssessmentAttemptCard";

const sortBySubmittedDesc = (
  a: ExitTicketAttemptDetail,
  b: ExitTicketAttemptDetail,
): number => {
  const aTime = a.submittedAt ? new Date(a.submittedAt).getTime() : 0;
  const bTime = b.submittedAt ? new Date(b.submittedAt).getTime() : 0;
  return bTime - aTime;
};

interface ExitTicketPopoverProps {
  data: ExitTicketData;
  assessmentLabel?: string;
  onViewDetails?: (attemptId: number) => void;
}

const ExitTicketPopover: React.FC<ExitTicketPopoverProps> = ({
  data,
  assessmentLabel = "exit ticket",
  onViewDetails,
}) => {
  const { attempts, totalAttempts, studentName } = data;
  const firstName = studentName.split(" ")[0];
  const {
    ref: scrollRef,
    hasMore: hasMoreBelow,
    onScroll,
    checkScroll,
  } = useScrollHint<HTMLDivElement>({ axis: "vertical" });

  useEffect(() => {
    requestAnimationFrame(checkScroll);
  }, [checkScroll]);

  if (attempts.length === 0) return null;

  const sorted = [...attempts].sort(sortBySubmittedDesc);
  const isMultiple = totalAttempts > 1;

  return (
    <div className="font-mulish text-sm">
      {hasMoreBelow && (
        <span className="block mb-1 font-mulish et-analytics-caption text-[10px] leading-none et-analytics-muted text-[#25252566]">
          Scroll to see all {totalAttempts} attempts ↓
        </span>
      )}
      <div
        ref={scrollRef}
        onScroll={onScroll}
        className="flex flex-col gap-3 max-h-[326px] overflow-y-auto px-1"
      >
        <div className="text-[#777777]">
          {isMultiple
            ? `${firstName} attempted ${assessmentLabel} ${totalAttempts} times`
            : `${firstName}\u2019s ${assessmentLabel}`}
        </div>
        {sorted.map((a, i) => (
          <AssessmentAttemptCard
            key={a.attemptNumber}
            attemptId={a.attemptId}
            attemptNumber={sorted.length - i}
            score={a.score}
            totalQuestions={a.totalQuestions}
            correctCount={a.correctCount}
            isBest={a.isBest}
            showHeader={isMultiple}
            onViewDetails={onViewDetails}
          />
        ))}
      </div>
    </div>
  );
};

export default ExitTicketPopover;
